import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '@/firebaseConfig';

const APP_ID = 'com.elev8ted.fuscofit';
const TRAINING_CHANNEL_ID = 'training-updates';
const TICKET_CHANNEL_ID = 'ticket-updates';
const ALLOWED_NOTIFICATION_PATHS = new Set(['/i-miei-allenamenti']);
const TICKET_NOTIFICATION_PATH = /^\/user\/conversation(?:-tecnico)?\/[A-Za-z0-9_-]{1,128}$/;

let activeUserId = null;
let activeToken = null;
let activeTokenRef = null;
let configured = false;
let routerInstance = null;
let pendingPath = null;
let syncPromise = Promise.resolve();

const isNative = () => Capacitor.isNativePlatform();

export function isAllowedNotificationPath(path) {
  return typeof path === 'string' && (
    ALLOWED_NOTIFICATION_PATHS.has(path) || TICKET_NOTIFICATION_PATH.test(path)
  );
}

function platformName() {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' ? 'ios' : 'android';
}

async function sha256(value) {
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

function notificationData(event) {
  const value = event?.notification?.data;
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
}

async function openNotificationPath(path) {
  if (!isAllowedNotificationPath(path)) return;
  if (!activeUserId || !routerInstance) {
    pendingPath = path;
    return;
  }
  await routerInstance.isReady();
  if (routerInstance.currentRoute.value.path !== path) await routerInstance.push(path);
  pendingPath = null;
}

async function saveToken(userId, token) {
  if (!userId || userId !== activeUserId || !token) return;
  const tokenHash = await sha256(token);
  const tokenRef = doc(db, 'users', userId, 'fcm_tokens', tokenHash);
  const existing = await getDoc(tokenRef);
  const payload = {
    fcm_token: token,
    tokenHash,
    platform: platformName(),
    appId: APP_ID,
    updatedAt: serverTimestamp(),
  };
  if (!existing.exists()) payload.createdAt = serverTimestamp();
  await setDoc(tokenRef, payload, { merge: true });

  const previousRef = activeTokenRef;
  activeToken = token;
  activeTokenRef = tokenRef;
  if (previousRef && previousRef.path !== tokenRef.path) {
    await deleteDoc(previousRef).catch(() => {});
  }
}

async function createAndroidChannels() {
  if (Capacitor.getPlatform() !== 'android') return;
  await Promise.all([
    FirebaseMessaging.createChannel({
      id: TRAINING_CHANNEL_ID,
      name: 'Aggiornamenti allenamenti',
      description: 'Nuovi allenamenti e aggiornamenti del tuo programma.',
      importance: 4,
      visibility: 1,
      vibration: true,
    }),
    FirebaseMessaging.createChannel({
      id: TICKET_CHANNEL_ID,
      name: 'Risposte assistenza',
      description: 'Nuove risposte dal team tecnico e nutrizionale.',
      importance: 4,
      visibility: 1,
      vibration: true,
    }),
  ]);
}

export async function configurePushNotifications(router) {
  if (!isNative() || configured) return;
  configured = true;
  routerInstance = router;

  await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
    if (!activeUserId || !token || token === activeToken) return;
    saveToken(activeUserId, token).catch(error => {
      console.warn('Token push non sincronizzato:', error);
    });
  });

  await FirebaseMessaging.addListener('notificationActionPerformed', event => {
    const path = notificationData(event).path;
    openNotificationPath(path).catch(error => {
      console.warn('Apertura notifica non riuscita:', error);
    });
  });

  await createAndroidChannels();
}

async function syncAuthenticatedUser(user) {
  if (!isNative() || !user?.uid) return { enabled: false, reason: 'not-native-or-signed-out' };
  activeUserId = user.uid;

  const support = await FirebaseMessaging.isSupported();
  if (!support.isSupported) return { enabled: false, reason: 'unsupported' };

  let permission = await FirebaseMessaging.checkPermissions();
  if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
    permission = await FirebaseMessaging.requestPermissions();
  }
  if (permission.receive !== 'granted') return { enabled: false, reason: 'permission-denied' };

  const { token } = await FirebaseMessaging.getToken();
  await saveToken(user.uid, token);
  if (pendingPath) await openNotificationPath(pendingPath);
  return { enabled: true };
}

export function syncPushNotificationsForUser(user) {
  syncPromise = syncPromise
    .catch(() => {})
    .then(() => syncAuthenticatedUser(user))
    .catch(error => {
      console.warn('Notifiche push non disponibili:', error);
      return { enabled: false, reason: 'configuration-error' };
    });
  return syncPromise;
}

export async function clearPushNotificationsForSignedOutUser() {
  if (!isNative()) return;
  activeUserId = null;
  activeToken = null;
  activeTokenRef = null;
  pendingPath = null;
  await FirebaseMessaging.deleteToken().catch(error => {
    console.warn('Token push locale non eliminato:', error);
  });
}
